/**
 * EliteEnemy - Tougher enemy variant with HP bar
 *
 * Features:
 * - Hexagon shape (6 sides)
 * - Higher HP (5-20, configurable)
 * - HP bar displayed above enemy
 * - Points = HP × 10
 * - Introduced at Level 5+
 */

import Phaser from 'phaser';
import { Enemy, EnemyConfig } from './Enemy';
import { COLORS } from '../config';
import { ENEMIES } from '../utils/constants';
import { getParticlePool } from '../systems/ParticlePool';

export interface EliteEnemyConfig extends EnemyConfig {
  hp: number; // Required for elite enemies (5-20)
}

export class EliteEnemy extends Enemy {
  /** Size of elite enemy (hexagon width/height) */
  static readonly SIZE = Enemy.SIZE * 1.2;

  /** HP bar background */
  private hpBarBg: Phaser.GameObjects.Graphics;

  /** HP bar fill */
  private hpBarFill: Phaser.GameObjects.Graphics;
  private readonly isFinaleEncounter: boolean;

  /** HP bar dimensions */
  private static readonly HP_BAR_WIDTH = 30;
  private static readonly HP_BAR_HEIGHT = 4;
  private static readonly HP_BAR_OFFSET_Y = -20; // Above the hexagon

  constructor(scene: Phaser.Scene, config: EliteEnemyConfig) {
    // Clamp HP to valid range
    const hp = Phaser.Math.Clamp(config.hp, ENEMIES.elite.minHp, ENEMIES.elite.maxHp);

    // Pass config to parent with clamped HP
    super(scene, { ...config, hp, speed: config.speed ?? ENEMIES.elite.speed });
    this.isFinaleEncounter = config.isFinale ?? false;

    // Set elite-specific points (HP × 10)
    this.points = hp * ENEMIES.elite.pointsPerHp;

    // Create HP bar (add before shape so it renders behind during death animation)
    this.hpBarBg = scene.add.graphics();
    this.hpBarFill = scene.add.graphics();
    this.drawHpBar();

    // Add HP bar to container (before shape)
    this.addAt(this.hpBarBg, 0);
    this.addAt(this.hpBarFill, 1);

    // Scale up elite sprite slightly for clarity
    this.sprite.setScale(Enemy.SCALE * (this.isFinaleEncounter ? 1.62 : 1.2));

    if (this.isFinaleEncounter) {
      this.hpBarBg.setScale(2.2, 1.35);
      this.hpBarFill.setScale(2.2, 1.35);
      const finaleLabel = scene.add.text(0, 38, 'FINAL TARGET', {
        fontFamily: 'Arial, sans-serif',
        fontSize: '10px',
        color: '#FFD93D',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 2,
      }).setOrigin(0.5);
      this.add(finaleLabel);
    }

    // Dark outline glow so the sprite reads clearly at small scale
    if (this.sprite.preFX) {
      this.sprite.preFX.addGlow(0x000000, 1, 0, false, 0.1, 16);
    }
  }

  /**
   * Draws the HP bar above the enemy
   */
  private drawHpBar(): void {
    const barWidth = EliteEnemy.HP_BAR_WIDTH;
    const barHeight = EliteEnemy.HP_BAR_HEIGHT;
    const offsetY = EliteEnemy.HP_BAR_OFFSET_Y;
    const cornerRadius = 2;

    // Background (dark)
    this.hpBarBg.clear();
    this.hpBarBg.fillStyle(0x000000, 0.5);
    this.hpBarBg.fillRoundedRect(
      -barWidth / 2,
      offsetY - barHeight / 2,
      barWidth,
      barHeight,
      cornerRadius
    );

    // Fill (enemy color) - width based on current HP
    const hpPercent = this.getHp() / this.getMaxHp();
    const fillWidth = barWidth * hpPercent;

    this.hpBarFill.clear();
    this.hpBarFill.fillStyle(COLORS.enemy, 1);
    this.hpBarFill.fillRoundedRect(
      -barWidth / 2,
      offsetY - barHeight / 2,
      fillWidth,
      barHeight,
      cornerRadius
    );
  }

  /**
   * Override takeDamage to update HP bar
   */
  takeDamage(damage: number): boolean {
    const died = super.takeDamage(damage);

    // Update HP bar if still alive
    if (!died) {
      this.updateHpBar();
    }

    return died;
  }

  /**
   * Updates the HP bar fill based on current HP
   */
  private updateHpBar(): void {
    const barWidth = EliteEnemy.HP_BAR_WIDTH;
    const barHeight = EliteEnemy.HP_BAR_HEIGHT;
    const offsetY = EliteEnemy.HP_BAR_OFFSET_Y;
    const cornerRadius = 2;

    const hpPercent = this.getHp() / this.getMaxHp();
    const fillWidth = Math.max(0, barWidth * hpPercent);

    this.hpBarFill.clear();
    this.hpBarFill.fillStyle(COLORS.enemy, 1);
    this.hpBarFill.fillRoundedRect(
      -barWidth / 2,
      offsetY - barHeight / 2,
      fillWidth,
      barHeight,
      cornerRadius
    );
  }

  /**
   * Gets the collision radius for this enemy (larger than basic)
   */
  getCollisionRadius(): number {
    return (EliteEnemy.SIZE / 2) * (this.isFinaleEncounter ? 1.35 : 1);
  }

  /**
   * Creates hexagon particle fragments on death
   * Uses particle pool for better performance
   */
  protected createDeathParticles(): void {
    const pool = getParticlePool();

    if (pool) {
      // Use pooled particles (performance optimized)
      pool.spawnBurst(this.x, this.y, 'hexagon', COLORS.enemy, Phaser.Math.Between(6, 10), {
        minSize: EliteEnemy.SIZE / 4,
        maxSize: EliteEnemy.SIZE / 2,
        minVelocity: 60,
        maxVelocity: 120,
        minDuration: 300,
        maxDuration: 400,
        alpha: 0.8,
      });
    } else {
      // Fallback: create particles manually (pool not initialized)
      const particleCount = Phaser.Math.Between(6, 10);
      const size = EliteEnemy.SIZE / 4;

      for (let i = 0; i < particleCount; i++) {
        const particle = this.scene.add.graphics();
        particle.fillStyle(COLORS.enemy, 0.8);

        // Small hexagon fragment
        particle.beginPath();
        for (let j = 0; j < 6; j++) {
          const angle = (j * Math.PI * 2) / 6 - Math.PI / 2;
          const x = Math.cos(angle) * size / 2;
          const y = Math.sin(angle) * size / 2;
          if (j === 0) {
            particle.moveTo(x, y);
          } else {
            particle.lineTo(x, y);
          }
        }
        particle.closePath();
        particle.fillPath();

        particle.setPosition(this.x, this.y);

        // Random angle and velocity
        const angle = (i / particleCount) * Math.PI * 2;
        const velocity = Phaser.Math.Between(60, 120);
        const vx = Math.cos(angle) * velocity;
        const vy = Math.sin(angle) * velocity;
        const rotation = Phaser.Math.FloatBetween(-Math.PI, Math.PI);

        // Animate particle outward and fade
        this.scene.tweens.add({
          targets: particle,
          x: particle.x + vx * 0.3,
          y: particle.y + vy * 0.3,
          alpha: 0,
          rotation: rotation,
          duration: 350,
          ease: 'Power2',
          onComplete: () => particle.destroy(),
        });
      }
    }
  }

  /**
   * Cleanup
   */
  destroy(fromScene?: boolean): void {
    this.hpBarBg?.destroy();
    this.hpBarFill?.destroy();
    super.destroy(fromScene);
  }
}
